import { msgpack, type Codec } from '@esengine/rpc/codec'
import { OnlineInvariantError, WireFormatError } from './errors'

/** The codec of every room connection, on both backends (the same msgpack framing as the game's own protocol). */
export function createOnlineCodec(): Codec {
  return msgpack()
}

/**
 * Encodes a packet into bytes the caller owns. msgpackr returns a view into its reusable scratch buffer, and a socket
 * may still hold the bytes after send() returns (Node `ws` queues them), so every frame is copied once.
 */
export function encodeFrame(codec: Codec, packet: Parameters<Codec['encode']>[0]): Uint8Array {
  const encoded = codec.encode(packet)
  if (typeof encoded === 'string') throw new OnlineInvariantError('the online codec must produce binary frames')
  return encoded.slice()
}

/**
 * Decodes one frame from a peer. This is the adapter edge of msgpackr, whose decode failures are untyped errors: the
 * try block holds only the decode call, so anything it throws is a malformed frame and becomes a WireFormatError.
 */
export function decodeFrame(codec: Codec, data: ArrayBuffer | Uint8Array | string): unknown {
  const bytes = data instanceof ArrayBuffer ? new Uint8Array(data) : data
  try {
    return codec.decode(bytes)
  } catch {
    throw new WireFormatError('frame is not a msgpack packet')
  }
}
